
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import ArticlePriceInfoDialog from "@/views/Dialogs/ArticlePriceInfoDialog.vue";
import ArticleEditDialog from "@/views/Dialogs/ArticleEditDialog.vue";
import ArticleDeleteWarningDialog from "@/views/Dialogs/ArticleDeleteWarningDialog.vue";
import SpeechBubbleFloat from "../Dialogs/SpeechBubbleFloat.vue";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import ArticleTileHeader from "@/views/Catalogue/ArticleTileComponents/ArticleTileHeader.vue";
import { TokenResponseDto } from "@/dtos/tokenResponseDto";
import { ArticleState } from "@/dtos/articleState";
import ArticleTilePricelist from "./ArticleTileComponents/ArticleTilePricelist.vue";
import rules from "@/store/rules";
import { CartItem } from "@/dtos/cartItem";
import { CheckArticleReason } from "@/dtos/checkArticleReason";
import SendToHelpdeskDialog from "@/views/Dialogs/SendToHelpdeskDialog.vue";
import TooManyCalculatingDialog from "@/views/Dialogs/TooManyCalculatingDialog.vue";
import ArticleTreatmentEditDialog from "@/views/Dialogs/ArticleTreatmentEditDialog.vue";
import IsPowdercoatingPossibleDialog from "@/views/Dialogs/IsPowdercoatingPossibleDialog.vue";
import CertificateHelperDialog from "@/views/Dialogs/CertificateHelperDialog.vue";
import ArticleZincCoatingEditDialog from "@/views/Dialogs/ArticleZincCoatingEditDialog.vue";
import { getEntgratartText } from "@/dtos/entgratart";
import { CertificateTypes } from "@/dtos/certificateTypes";
import { AssemblyInfoDto } from "@/dtos/assemblyInfoDto";
import { getZincCoatingText } from "@/dtos/zincCoating";

@Component({
	components: {
		ArticlePriceInfoDialog,
		ArticleEditDialog,
		ArticleDeleteWarningDialog,
		SendToHelpdeskDialog,
		TooManyCalculatingDialog,
		ArticleTreatmentEditDialog,
		ArticleZincCoatingEditDialog,
		CertificateHelperDialog,
		SpeechBubbleFloat,
		ArticleTileHeader,
		ArticleTilePricelist,
		IsPowdercoatingPossibleDialog,
	},
})
export default class ArticleTile extends Vue {
	@Prop()
	private articleId!: number;
	@Prop()
	private isAssembly!: boolean;
	@Prop()
	private isAssemblyView!: boolean;
	@Prop()
	private selectedSubarticleIds!: number[];
	@Prop()
	private isDeleteDisabled!: boolean;

	private amountToAddToCart: number = 1;
	private showState: boolean = false;
	private cadDialog: boolean = false;
	private currentWindowHeight: string = "100px";
	private deleteOverlay: boolean = false;
	private articleDeleteWarningDialog: boolean = false;
	private articleCartNames: string[] = [];
	private articleEditDialog: boolean = false;
	private articleToken: TokenResponseDto | null = null;
	private isValidCartAmount: boolean = true;
	private allowedCartAmountRule = rules.allowedCartAmountRule;
	private showHelpdeskDialogue: boolean = false;
	private reasonToSendArticleToHelpdesk: CheckArticleReason = CheckArticleReason.ModelError;
	private tooManyCalculatingDialog: boolean = false;
	private articleTreatmentEditDialog: boolean = false;
	private certificateHelperDialog: boolean = false;
	private getEntgratartText = getEntgratartText;
	private assemblyInfo: AssemblyInfoDto | null = null;
	private isPowdercoatingPossibleDialog: boolean = false;
	private editZincCoatingDialog: boolean = false;
	private onlyEditMaterial: boolean = false;

	private mounted() {
		if (this.isAssembly) {
			this.$store
				.dispatch("loadMissingArticles", this.article.subArticleIds)
				.then(() => {
					// The sub-article(s) may already have been cached from before (e.g. a previous
					// catalogue reload), in which case loadMissingArticles resolves without changing
					// state and the @Watch("subArticles") below never fires. Run the check directly
					// here as well so the assembly-by-subarticle swap still happens in that case.
					this.onSubArticlesChanged();
					this.loadAssemblyInfo(this.articleId);
				})
				.then(() => {
					this.showState = true;
				});
		} else {
			this.showState = true;
		}
		this.setWindowHeight();
		window.addEventListener("resize", (event) => {
			this.setWindowHeight();
		});
		if (this.article.quantityInAssembly != null) {
			this.amountToAddToCart = this.article.quantityInAssembly;
		}
	}

	private get article(): CustomerArticleDto {
		return this.$store.state.articles.find(
			(a: CustomerArticleDto) => a.articleId == this.articleId
		);
	}

	private get subArticles(): CustomerArticleDto[] {
		if (!this.isAssembly) return [];
		return this.$store.state.articles.filter(
			(a: CustomerArticleDto) => a.parentArticleId == this.articleId
		);
	}

	@Watch("subArticles")
	private onSubArticlesChanged() {
		// If it is an assembly with only one subarticle, and the subarticle has been loaded, the subarticle is shown instead of the assembly
		if (this.isAssembly && this.article.subArticleIds.length == 1 && 
			this.subArticles.some(a => a.articleId === this.article.subArticleIds[0])) {
			this.$store.commit("replaceAssemblyInCatalogBySubarticle", {
				assemblyArticleId: this.article.articleId,
				subArticleId: this.article.subArticleIds[0],
			});
		}
	}

	// This is a helper getter to quickly print state to the console
	private get singleArticleState() {
		return {
			state: this.article.state,
			calculating: this.isCalculating,
			locked: this.isLocked,
			migrated: this.isMigrated,
			canBeProduced: this.canBeProduced,
		};
	}

	private get assemblyState() {
		const locked: boolean =
			this.article.locked || this.subArticles.some((a: CustomerArticleDto) => a.locked);
		const calculating =
			this.article.calculating ||
			this.subArticles.some((a: CustomerArticleDto) => a.calculating);

		let state = ArticleState.OK;
		if (
			this.subArticles.length <= 0 ||
			this.subArticles.some((a: CustomerArticleDto) => 
				a.state == ArticleState.Error || 
				(a.state == ArticleState.New && !a.calculating))
		) {
			state = ArticleState.Error;
		} else if (this.subArticles.some((a: CustomerArticleDto) => a.state == ArticleState.Info)) {
			state = ArticleState.Info;
		} else if (
			this.subArticles.some((a: CustomerArticleDto) => a.state == ArticleState.Warning)
		) {
			state = ArticleState.Warning;
		}
		const canBeProduced = this.subArticles.every((a: CustomerArticleDto) => a.canBeProduced);
		const migrated =
			this.article.migrated || this.subArticles.some((sa: CustomerArticleDto) => sa.migrated);
		const returnState = {
			locked: locked,
			migrated: migrated,
			state: state,
			calculating: calculating,
			canBeProduced: canBeProduced,
		};
		return returnState;
	}

	private get state() {
		return this.isAssembly ? this.assemblyState.state : this.article.state;
	}

	private get isCalculating() {
		return this.isAssembly ? this.assemblyState.calculating : this.article.calculating;
	}

	private get isLocked() {
		return this.isAssembly ? this.assemblyState.locked : this.article.locked;
	}

	private get isMigrated() {
		return this.isAssembly ? this.assemblyState.migrated : this.article.migrated;
	}

	private get canBeProduced() {
		return this.isAssembly ? this.assemblyState.canBeProduced : this.article.canBeProduced;
	}

	private get assemblyGewicht() {
		return this.subArticles.reduce((previousValue, a) => {
			return previousValue + a.weight * a.quantityInAssembly;
		}, 0);
	}

	private get hasEngravings() {
		return this.isAssembly
			? this.subArticles.some((sa: CustomerArticleDto) => sa.hasEngravings)
			: this.article.hasEngravings;
	}

	private get hasWeldlines() {
		return this.article.weldlines != null && this.article.weldlines.length > 0;
	}

	private get materialIsUnavailable(): boolean {
		// Put an overlay over the whole article tile if the material of this article
		// is not available.
		// In case the article/assembly has a special thickness that NO material has,
		// don't show the overlay.

		// If this article or subarticles of an assembly are calculating,
		// the material is not considered unavailable.
		// also if the material id is -1, it's not considered unavailable.
		if (!this.isAssembly) {
			if (this.article.calculating || this.article.materialId <= 0) return false;
			const material = this.$store.getters.getMaterial(this.article.materialId);
			return material === undefined;
		} else {
			if (this.article.calculating || this.subArticles.some((sa) => sa.calculating))
				return false;
			return this.subArticles.some(
				(sa) =>
					sa.materialId > 0 &&
					this.$store.getters.getMaterial(sa.materialId) === undefined
			);
		}
	}

	private get isPowderCoatingAvailable() {
		return this.$store.state.isPowderCoatingAvailable;
	}

	private get isZincCoatingAvailable() {
		return this.$store.state.isZincCoatingAvailable;
	}

	private get articleHasPowderCoating() {
		// Powdercoating is assigned to assembly, not subarticles
		return this.article.powderCoatingTreatment != null;
	}

	private get powderCoatingText() {
		if (!this.articleHasPowderCoating) return this.$t("articleTile.info.noCoating") as string;
		return `RAL\u00A0${this.article.powderCoatingTreatment?.powder.surfaceColor.ralCode}`;
	}

	private get cadTooltipText(): string {
		return `${this.$t("articleTile.cad.tooltipLine1")} ${this.$t(
			"articleTile.cad.tooltipLine2"
		)} ${this.$t("articleTile.cad.tooltipLine3")}`;
	}

	private get zincCoatingText(): string {
		return getZincCoatingText(this.article.zincCoating);
	}

	private get hasFeatures() {
		if (this.isAssembly) {
			return (
				this.article.powderCoatingTreatment != null ||
				this.article.zincCoating > 0 ||
				this.article.weldlines.length > 0
			);
		} else {
			return (
				this.article.powderCoatingTreatment != null ||
				this.article.featureDtos.length > 0 ||
				this.article.hasEngravings ||
				this.article.zincCoating > 0 ||
				this.article.weldlines.length > 0
			);
		}
	}

	private get canArticleBeEdited() {
		return !this.isArticleLockedAndNotAdmin && !this.isCalculating;
	}

	private get isArticleLockedAndNotAdmin() {
		if (this.$store.state.customer.user.isAdmin || this.isImpersonated) {
			return false;
		}
		return this.isLocked;
	}

	private get isArticleUnlockedAndAdmin() {
		return !this.isLocked && (this.$store.state.customer.user.isAdmin || this.isImpersonated);
	}

	private get isArticleLockedAndAdmin() {
		return this.isLocked && (this.$store.state.customer.user.isAdmin || this.isImpersonated);
	}

	private get isImpersonated() {
		var oldToken = window.localStorage.getItem(
			`oidc.user:${process.env.VUE_APP_authorityUrl}:BlexonKundenportalClient_`
		);
		if (oldToken != null) {
			return true;
		}
		return false;
	}

	private get region() {
		return this.$store.getters.customerDefaultDeliveryAddress.country;
	}

	private get isStatusChipLinkToCad() {
		return (
			(this.state === ArticleState.Warning ||
				this.state === ArticleState.Error ||
				this.state === ArticleState.Info) &&
			!this.isMigrated
		);
	}

	private get isCartButtonRowVisible() {
		if (this.isCalculating) return false;
		if (this.isMigrated) return false;
		if (this.isLocked) return false;
		if (this.isAssemblyView) return false;
		return this.state === ArticleState.OK || this.state === ArticleState.Warning;
	}

	private get isCartButtonEnabled() {
		if (this.isAssemblyView) {
			// Articles can't be added to the cart if they're part of an assembly
			return false;
		}
		if (
			!this.isValidCartAmount ||
			!this.canBeProduced ||
			this.isCalculating ||
			(this.state !== ArticleState.OK && this.state !== ArticleState.Warning)
		) {
			return false;
		}
		return true;
	}

	private get isHelpdeskButtonVisible() {
		if (this.isAssembly) return false;
		if (this.isMigrated || this.isCalculating) {
			return false;
		}
		return this.state === ArticleState.Error || this.isLocked;
	}

	private get isHelpdeskButtonDisabled() {
		if (this.isCalculating) return false;
		return this.isLocked;
	}

	private get isCadButtonVisible() {
		return !this.isCalculating && !this.isMigrated;
	}

	private getThumbnailUrl(id: number) {
		return this.$store.getters.getThumbnailUrl(id, this.article.lastChange);
	}

	private navigateTo(link: string) {
		this.$router.push({ path: link });
	}

	private get articleCadViewUrl() {
		// The cache buster has to sit BEFORE the "#": everything after it is a URL fragment, which
		// the browser neither sends to the server nor uses as part of its cache key. A tick behind
		// the hash therefore busts nothing - the browser keeps serving a stale index.html from its
		// cache and with it the stale (hashed) asset references.
		// The language stays behind the hash: the cam client reads it from the hash route query.
		const [origin, route = "/"] = String(process.env.VUE_APP_CAM_CLIENT_URL).split("#");
		return (
			`${origin}?tick=${new Date().getTime()}#${route}` +
			`${this.article.articleId}/${this.articleToken?.token}` +
			`?language=${this.$i18n.locale}`
		);
	}

	private get certificates(): string[] {
		const material = this.$store.getters.getMaterial(this.article.materialId);
		if (material === undefined) {
			return [];
		}
		if (material.certificateTypes !== CertificateTypes.None) {
			return ["3.1"];
		} else {
			return [];
		}
	}
	private get certificate(): boolean {
		return this.article.certificateTypes > 0;
	}
	private set certificate(value: boolean) {
		if (value) {
			this.article.certificateTypes = CertificateTypes.ThreeDotOne;
		} else {
			this.article.certificateTypes = CertificateTypes.None;
		}
		// This sends an article update via SignalR
		// So the article doesn't have to be updated manually in the frontend.
		httpClient().post(
			`article/SetCertificate?articleId=${this.article.articleId}&certificateTypes=${this.article.certificateTypes}`
		);
	}

	private get progressPercent() {
		const progress = this.$store.state.validationProgresses.filter(
			(vp: any) => vp.articleId === this.articleId
		)[0];
		if (progress && progress.totalSteps > 0) {
			return Math.ceil((progress.completedSteps / progress.totalSteps) * 100);
		} else {
			return 3; // Show at least a bit of progress
		}
	}

	private get hasProgress() {
		return this.$store.state.validationProgresses.some(
			(vp: any) => vp.articleId === this.articleId
		);
	}

	private get assemblyMaterialText(): string {
		if (!this.subArticles || this.subArticles.length <= 0) return "";
		// If some materials or werkstoffe are not defined, print "Divers"
		if (
			this.subArticles.some(
				(a: CustomerArticleDto) => a.materialId <= 0 || a.werkstoffId <= 0
			)
		)
			return this.$t("articleTile.info.materialDivers") as string;
		// If all articles have the same material (same thickness), print exact material.
		if (
			this.subArticles.every(
				(a: CustomerArticleDto) => a.materialId === this.subArticles[0].materialId
			)
		) {
			return this.subArticles[0].materialname;
		}
		// If all articles have the same werkstoff but not the same thickness, print werkstoff and range of thicknesses.
		if (
			this.subArticles.every(
				(a: CustomerArticleDto) => a.werkstoffId === this.subArticles[0].werkstoffId
			)
		) {
			const werkstoffName = this.subArticles[0].werkstoffname;
			const thicknesses = this.subArticles
				.map((a: CustomerArticleDto) => Math.round(a.thickness * 2) / 2)
				.sort((a, b) => a - b);
			return (
				werkstoffName +
				" " +
				this.formatThickness(thicknesses[0]) +
				" - " +
				this.formatThickness(thicknesses[thicknesses.length - 1]) +
				"mm"
			);
		}
		return this.$t("articleTile.info.materialDivers") as string;
	}

	private formatThickness(thickness: number): string {
		return Number.isInteger(thickness) ? thickness.toString() : thickness.toFixed(1);
	}

	private setDeleteOverlay(value: boolean) {
		this.deleteOverlay = value;
	}

	private addToCart(quantity: number | null) {
		let newCartItem = new CartItem();
		const quantityToAdd = quantity ? quantity : this.amountToAddToCart;
		newCartItem.articleId = this.articleId;
		newCartItem.quantity = parseInt(quantityToAdd as any);
		this.$store.dispatch("addToCart", newCartItem);
		this.$store.dispatch("setSnackbarArticleText", {
			text: this.$t("articleTile.cart.addedToCartSnackbar", { quantity: quantityToAdd }),
			articleId: this.articleId,
		});
		if (this.article.quantityInAssembly != null) {
			this.amountToAddToCart = this.article.quantityInAssembly;
		} else {
			this.amountToAddToCart = 1;
		}
	}

	private addQuantityToCart(quantity: number) {
		console.log(`Adding to cart with quantity: ${quantity}`);
		this.addToCart(quantity);
	}

	private async checkDeleteArticle() {
		try {
			this.articleCartNames = [];
			// With old assemblies it was possible to add a subarticle to a saved cart.
			const response = await httpClient().get(
				`Cart/GetCartsOfArticle?articleId=${this.articleId}`
			);
			if (response.data.length > 0) {
				this.articleCartNames = response.data.sort();
			}

			// If this is a subarticle, load the assembly info to find out if the
			// article to be deleted is welded to any others
			if (this.isAssemblyView) {
				await this.loadAssemblyInfo(this.article.parentArticleId);
			}
			this.articleDeleteWarningDialog = true;
		} catch (err) {
			console.error(`Could not retrieve carts of article ${this.articleId}.`);
			this.$store.dispatch(
				"setSnackbarErrorText",
				this.$t("articleTile.messages.checkCartMembershipError")
			);
		}
	}

	private addOrRemoveSelectedSubarticleId() {
		// This is only triggered if this article is part of an assembly viewed in the assembly view
		this.$emit("addOrRemoveSelectedSubarticleId", this.article.articleId);
	}

	private async revalidate() {
		try {
			this.$store.commit("setArticleStateToCalculating", this.articleId);
			const response = await httpClient().post(
				`article/revalidate?articleId=${this.articleId}`
			);
			if (response.data === false) {
				// User is calculating too many articles at once
				this.tooManyCalculatingDialog = true;
			}
		} catch (err) {
			console.log(`Fehler beim erneuten Validieren des Artikels. Error: ${err}`);
		}
	}

	private async loadAssemblyInfo(articleId: number) {
		try {
			const response = await httpClient().get(
				`article/GetAssemblyInfo?articleId=${articleId}`
			);
			this.assemblyInfo = response.data;
		} catch (ex) {
			console.log("Assembly info could not be loaded for assembly " + this.articleId);
		}
	}

	private async openCadDialog(event: PointerEvent) {
		try {
			if (this.isCalculating || this.isMigrated) {
				return;
			}
			const response = await httpClient().post(
				`article/GetArticleToken?articleId=${this.articleId}`
			);
			this.articleToken = response.data;
			if (this.articleToken != null) {
				window.addEventListener("message", this.handleArticleChangedInCad);
				this.cadDialog = true;
			}
		} catch (err) {
			console.log(`Für den Artikel konnte kein Token geladen werden. Error: ${err}`);
		}
	}

	/**
	 * This event handler is called, when the cad article in the iframe has changed.
	 */
	private handleArticleChangedInCad(event: any) {
		if (
			event.origin == "https://cad.test.blexon.com" ||
			event.origin == "https://cam.test.blexon.com" ||
			event.origin == "https://cad.blexon.com" ||
			event.origin == "https://cam.blexon.com" ||
			event.origin == "http://localhost:8180" ||
			event.origin == "http://localhost:8185"
		) {
			if (event.data == `article-${this.articleId}-closed`) {
				this.closeCadDialog(true);
			} else if (event.data == `article-${this.articleId}-saved`) {
				this.closeCadDialog(false);
			}
		}
	}

	private async closeCadDialog(deleteToken: boolean) {
		console.log("closing cad dialog");
		this.cadDialog = false;
		window.removeEventListener("message", this.handleArticleChangedInCad);
		// delete token only when the article was not saved.
		// Wenn der Artikel gespeichert wurde, wird das Token nach der Validierung gelöscht.
		if (deleteToken) {
			console.log("deleting token", this.articleToken?.token);
			await httpClient().post(`article/DeleteToken?token=${this.articleToken?.token}`);
		}
		this.articleToken = null;
	}

	private setWindowHeight() {
		// 165 besteht aus...
		// - Margins der Dialogbox (variieren etwas je nach Aspect Ratio)
		// - Buttons für Speichern / Schliessen im Dialogfenster
		// - Sicherheitsmarge, sodass sicher keine Scrollbar im Dialog erscheint
		const currentHeight = window.innerHeight - 65;
		this.currentWindowHeight = `${currentHeight}px`;
	}

	private openArticleEditDialog(onlyMaterial: boolean) {
		if (this.canArticleBeEdited) {
			this.onlyEditMaterial = onlyMaterial;
			this.articleEditDialog = true;
		}
	}

	private openSubarticles() {
		// Remember the scroll position for when getting back to the article dashboard.
		this.$store.commit("setArticleScrollPosition", window.scrollY);
		this.navigateTo(`catalogue/assembly/${this.article.articleId}`);
	}

	/**
	 * Creates a text to show at the user interface to inform the user
	 * about the weldline features.
	 */
	private getWeldlineString(): string {
		return this.$store.getters.getWeldlineString(this.article.weldlines);
	}

	private getZusatzbezeichnungOberseite() {
		if (this.article.materialname != null) {
			if (this.article.materialname.includes("geschliffen")) {
				return this.$t("articleTile.info.additionalDesignationGrinded") as string;
			}
			if (this.article.materialname.includes("eloxiert")) {
				return this.$t("articleTile.info.additionalDesignationAnodized") as string;
			}
		}
		return "";
	}

	private getZusatzbezeichnungUnterseite() {
		if (this.article.materialname != null) {
			if (this.article.materialname.includes("Riffel")) {
				return this.$t("articleTile.info.additionalDesignationRibbed") as string;
			}
		}
		return "";
	}

	private getFinishText(value: number) {
		return this.$store.getters.getPowdercoatingFinishText(value);
	}

	private getStructureText(value: number) {
		return this.$store.getters.getPowdercoatingStructureText(value);
	}

	private get articleStateText() {
		if (this.isLocked) {
			return this.$t("articleTile.state.inReview") as string;
		}
		switch (this.state) {
			case ArticleState.OK: {
				return this.$t("articleTile.state.ok") as string;
			}
			case ArticleState.Warning: {
				return this.$t("articleTile.state.warning") as string;
			}
			case ArticleState.Error: {
				return this.$t("articleTile.state.error") as string;
			}
			case ArticleState.New: {
				return this.$t("articleTile.state.new") as string;
			}
			case ArticleState.Info: {
				return this.$t("articleTile.state.actionRequired") as string;
			}
			default: {
				return this.$t("articleTile.state.unclear") as string;
			}
		}
	}
	private get articleStateIcon() {
		if (this.isLocked) {
			return "mdi-lock";
		}
		switch (this.state) {
			case ArticleState.OK: {
				return "mdi-check";
			}
			case ArticleState.Warning: {
				return "mdi-exclamation";
			}
			case ArticleState.Error: {
				return "mdi-close";
			}
			case ArticleState.New: {
				return "mdi-close";
			}
			default: {
				return "mdi-help";
			}
		}
	}
	private get articleStateColor() {
		if (this.isLocked) {
			return "#CFD8DC";
		}
		switch (this.state) {
			case ArticleState.OK: {
				return "#C8E6C9";
			}
			case ArticleState.Warning: {
				return "#FFF9C4";
			}
			case ArticleState.Error: {
				return "#FFCDD2";
			}
			case ArticleState.Info: {
				return "#efbb20";
			}
			default: {
				return "#FFCDD2";
			}
		}
	}
}
